import { JobLiquidityWave } from "./JobLiquidityWave";

import { Column, OptionalTooltip, Row, Text } from "@/components";
import { LiquidityBreakdownCard } from "@/components/dashboard/cells/LiquidityBreakdownCard";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useGetJobPipelineMetrics } from "@/hooks/queries/useGetJobPipelineMetrics";
import { screenTimeColorClass } from "@/lib/constants";
import { liquidity as liquidityUtils } from "@/lib/liquidity";
import { cn } from "@/lib/utils";
import {
  JobLiquidity,
  JobLiquidityBreakdown,
  StafferFirstScreenStats,
} from "@/types";

const LABEL_CLASS =
  "text-xs font-medium uppercase leading-none tracking-wide text-slate-500";

// One pill per requisition; the first `filled` (placed reqs) are colored.
const PlacementsPills = ({
  filled,
  total,
}: {
  filled: number;
  total: number;
}) => {
  if (total === 0) {
    return <Row className="h-2 w-14 rounded-full bg-slate-200" />;
  }
  return (
    <Row className="max-w-[160px] items-center gap-1">
      {Array.from({ length: total }).map((_, i) => (
        <Row
          key={i}
          className={cn(
            "h-2 w-7 min-w-0 shrink rounded-full",
            i < filled ? "bg-primary-600" : "bg-slate-200",
          )}
        />
      ))}
    </Row>
  );
};

// Median hours to first screen of candidacy
const TimeToScreenMetric = ({
  medianHours,
  byStaffer,
}: {
  medianHours: number | null;
  byStaffer: StafferFirstScreenStats[];
}) => {
  /* Render */
  const renderBreakdown = () => (
    <Column className="w-full gap-1">
      {[...byStaffer]
        .sort((a, b) => b.medianHours - a.medianHours)
        .map((staffer) => (
          <Row
            key={staffer.userId}
            className="w-full shrink-0 items-center justify-between gap-4"
          >
            <Text className="truncate text-xs leading-none text-slate-700">
              {staffer.name} ({staffer.screenCount.toString()})
            </Text>
            <Text
              className={cn(
                "text-xs font-semibold tabular-nums leading-none",
                screenTimeColorClass(staffer.medianHours),
              )}
            >
              {staffer.medianHours.toString()}h
            </Text>
          </Row>
        ))}
    </Column>
  );

  const renderMedian = () => {
    if (medianHours === null) {
      return (
        <Text className="text-3xl font-bold leading-none text-slate-300">
          —
        </Text>
      );
    }
    return (
      <Text
        className={cn(
          "text-3xl font-bold tabular-nums leading-none",
          screenTimeColorClass(medianHours),
        )}
      >
        {medianHours.toString()}h
      </Text>
    );
  };

  const renderMetric = () => (
    <Column className="w-fit items-start gap-1.5">
      <Text className={LABEL_CLASS}>Time to Screen</Text>
      <Row className="h-8 items-center">{renderMedian()}</Row>
    </Column>
  );

  const render = () => {
    if (byStaffer.length === 0) return renderMetric();

    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>{renderMetric()}</TooltipTrigger>
          <TooltipContent className="w-44 p-2" side="bottom">
            {renderBreakdown()}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return render();
};

// Persistent job-page header strip: placements vs reqs, liquidity, submits.
export const JobPipelineHeaderMetrics = ({
  companyId,
  jobId,
  jobLiquidity,
  jobLiquidityBreakdown,
}: {
  companyId: string;
  jobId: string;
  jobLiquidity?: JobLiquidity | null;
  jobLiquidityBreakdown?: JobLiquidityBreakdown | null;
}) => {
  const { data: metrics } = useGetJobPipelineMetrics({ companyId, jobId });

  const render = () => {
    if (!metrics) return null;

    const liquidity = jobLiquidity ?? JobLiquidity.UNKNOWN;

    return (
      <Row className="shrink-0 items-start gap-10 pb-2.5">
        <TimeToScreenMetric
          byStaffer={metrics.firstScreensByStaffer}
          medianHours={metrics.medianHoursToFirstScreen}
        />

        <Column className="items-start gap-1.5 border-l border-slate-200 pl-8">
          <Text className={LABEL_CLASS}>Placements</Text>
          <Row className="h-8 items-center gap-2">
            <PlacementsPills
              filled={metrics.placementsCount}
              total={metrics.reqsTotal}
            />
            <Text className="text-sm tabular-nums leading-none text-slate-500">
              {metrics.placementsCount.toString()} /{" "}
              {metrics.reqsTotal.toString()} reqs
            </Text>
          </Row>
        </Column>

        <OptionalTooltip
          contentClassName="w-80 whitespace-normal p-0"
          text={
            <LiquidityBreakdownCard
              breakdown={jobLiquidityBreakdown}
              level={liquidity}
            />
          }
          trackingLabel="Job header liquidity tooltip"
        >
          <Column className="relative -mr-10 items-start gap-1.5 border-l border-slate-200 pl-8 pr-10">
            <JobLiquidityWave
              className="-bottom-2.5 -left-px -right-px z-20"
              liquidity={liquidity}
            />
            <Text className={cn("relative z-30", LABEL_CLASS)}>Liquidity</Text>
            <Row className="relative z-30 h-8 items-center gap-2">
              <Text className="text-lg font-semibold tabular-nums leading-none text-primary-700">
                {liquidityUtils.label(liquidity)}
              </Text>
            </Row>
          </Column>
        </OptionalTooltip>

        <Column className="items-start gap-1.5 border-l border-slate-200 pl-8">
          <Text className={LABEL_CLASS}>Submits</Text>
          <Row className="h-8 items-center">
            <Text className="text-3xl font-bold tabular-nums leading-none text-slate-900">
              {metrics.submitsCount.toLocaleString()}
            </Text>
          </Row>
        </Column>
      </Row>
    );
  };

  return render();
};
